import { useEffect, useState } from 'react';
import { adminSecurityApi, AdminApiError, type AdminChallengeOption } from '../../api/admin.ts';
import { requestAssertion, wasDismissed } from '../../lib/webauthn.ts';
import { Alert, Button, TextInput } from '../ui.tsx';

/**
 * "Confirm it's you" for the operator console — the way THROUGH the freshness
 * gate rather than an apology for it.
 *
 * Every sensitive operator action reads `elevated_at`, and an ordinary MFA
 * sign-in deliberately never sets it: proving a factor to get in is not the
 * same as proving one in front of a payout. Only this dialog and the
 * enrollment flow set it. Without it, an operator's five-minute enrollment
 * stamp expired and adding a factor, removing one, regenerating recovery codes
 * and executing a payout all began answering `FRESH_AUTH_REQUIRED` forever —
 * including after signing out and back in, which is what makes the dead end so
 * confusing.
 */
export function isAdminFreshAuthRequired(error: unknown): boolean {
  return error instanceof AdminApiError && error.code === 'FRESH_AUTH_REQUIRED';
}

export default function AdminElevateDialog({ onVerified, onCancel }: { onVerified: () => void; onCancel: () => void }) {
  const [options, setOptions] = useState<AdminChallengeOption[] | null>(null);
  const [selected, setSelected] = useState<AdminChallengeOption | null>(null);
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function start(option: AdminChallengeOption) {
    if (busy) return;
    setSelected(option);
    setCode('');
    setError(null);
    setBusy(true);
    try {
      const started = await adminSecurityApi.elevateChallenge({ type: option.type, methodId: option.methodId });
      setChallengeId(started.challengeId);
      if (option.type === 'webauthn' && started.publicKey) {
        const assertion = await requestAssertion(started.publicKey);
        // A dismissed prompt is a choice; the challenge stays live.
        if (!assertion) return;
        await adminSecurityApi.elevateVerify({ challengeId: started.challengeId, assertion });
        onVerified();
      }
    } catch (caught) {
      if (!wasDismissed(caught)) {
        setError(caught instanceof AdminApiError ? caught.message : 'That did not work. Please try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    let canceled = false;
    adminSecurityApi
      .elevateOptions()
      .then((result) => {
        if (canceled) return;
        setOptions(result.options);
        // Started rather than merely checked, for the same reason the sign-in
        // form starts its first option: a checked radio fires no `onChange`,
        // so an operator with one method would face a dialog with no input.
        const first = result.options[0];
        if (first) void start(first);
      })
      .catch(() => {
        if (!canceled) setError('Verification is unavailable right now.');
      });
    return () => {
      canceled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function submit() {
    if (busy || !challengeId) return;
    setBusy(true);
    setError(null);
    try {
      await adminSecurityApi.elevateVerify({ challengeId, code });
      onVerified();
    } catch (caught) {
      setCode('');
      setError(caught instanceof AdminApiError ? caught.message : 'That code is not right.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded border border-amber-300 bg-amber-50 p-4">
      <h2 className="font-semibold">Confirm it&rsquo;s you</h2>
      <p className="text-sm">This change needs a fresh check, even though you are already signed in.</p>

      {error ? (
        <div className="mt-3">
          <Alert tone="error">{error}</Alert>
        </div>
      ) : null}

      {!options && !error ? (
        <p className="mt-3 text-sm" role="status">
          Loading verification methods…
        </p>
      ) : null}

      {!challengeId && selected?.type !== 'webauthn' ? (
        <Button type="button" variant="ghost" className="mt-3" onClick={onCancel}>
          Cancel
        </Button>
      ) : null}

      {options && options.length > 1 ? (
        <fieldset className="mt-3 space-y-1">
          {options.map((option) => (
            <label key={`${option.type}:${option.methodId ?? 'none'}`} className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="admin-elevate-method"
                checked={selected?.methodId === option.methodId && selected?.type === option.type}
                onChange={() => void start(option)}
              />
              <span>{option.label}</span>
            </label>
          ))}
        </fieldset>
      ) : null}

      {challengeId && selected?.type !== 'webauthn' ? (
        <div className="mt-3 space-y-3">
          <TextInput
            id="admin-elevate-code"
            label="Code"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            inputMode={selected?.type === 'recovery' ? 'text' : 'numeric'}
            autoComplete={selected?.type === 'recovery' ? 'off' : 'one-time-code'}
            autoFocus
          />
          <Button type="button" onClick={() => void submit()} disabled={busy || code.length === 0}>
            Confirm
          </Button>{' '}
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      ) : null}

      {selected?.type === 'webauthn' ? (
        <div className="mt-3">
          <Button type="button" onClick={() => void start(selected)} disabled={busy}>
            Use your security key
          </Button>{' '}
          <Button type="button" variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      ) : null}
    </div>
  );
}
